import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams, useNavigate, NavLink, Link } from "react-router-dom";
import { Header } from "./components/Header";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import { type } from "@testing-library/user-event/dist/type";

const API_URL = "/api";

export default function InterestCalculations() {
  const { id } = useParams();
  console.log("🔎 Route id:", id);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [company, setCompany] = useState(null);
  const [transactionData, setTransactionData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checklistFormData, setChecklistFormData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [scheduleData, setScheduleData] = useState([]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getTransactionDetails/${id}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        console.log("📥 Full API Response:", data);
        setTransactionData(data);
        console.log("transactionData:", transactionData);
        // setFormData(data?.DocumentCheckList || {});
        setFormData(data); // keep full object for Loan Info
        setCompany(data);
        console.log("Data:", data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, []);
  useEffect(() => {
    if (transactionData) {
      const start = new Date(
        transactionData?.CONTRACTSTARTDATE || "2025-01-01"
      );
      const end = new Date(transactionData?.CONTRACTENDDATE || "2025-12-31");

      const rows = [];
      let current = new Date(start);

      while (current <= end) {
        rows.push({
          Date: current.toISOString().split("T")[0], // yyyy-mm-dd
          Disbursement: 0,
          Repayment: 0,
          OutstandingBalance: 0,
          InterestAccrued: 0,
          OverdueCharges: 0,
        });

        // ✅ Move 1 day forward
        current.setDate(current.getDate() + 1);
      }

      setScheduleData(rows);
    }
  }, [transactionData]);

  // helper: last day of a month
  const getLastDayOfMonth = (date) => {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  };

  const navigate = useNavigate();
  const contractStartDate = transactionData?.CONTRACTSTARTDATE || "2025-01-01";
  const contractEndDate = transactionData?.CONTRACTENDDATE || "2025-12-31";

  const handleChange = (index, field, value) => {
    const updated = [...scheduleData];

    // Date validation
    if (field === "Date") {
      const selectedDate = new Date(value);
      const start = new Date(contractStartDate);
      const end = new Date(contractEndDate);

      if (selectedDate < start || selectedDate > end) {
        alert("Please select a date within the contract period!");
        return;
      }
      updated[index][field] = value;
    } else {
      updated[index][field] = Number(value) || 0;
    }

    // Constants (Excel equivalent)
    const interestRate = Number(transactionData?.INTERESTRATE || 0); // % per year
    const overdueRate = 0.1; // 10% annual

    // 🔁 Recalculate all rows in sequence
    let prevBalance = 0;
    for (let i = 0; i < updated.length; i++) {
      const row = updated[i];
      const disb = Number(row.Disbursement) || 0;
      const repay = Number(row.Repayment) || 0;

      // Outstanding Balance
      row.OutstandingBalance = prevBalance + disb - repay;

      // Interest Accrued = Outstanding * Rate / 365
      row.InterestAccrued = row.OutstandingBalance * (interestRate / 100 / 365);

      // Overdue Charges = Outstanding * OverdueRate / 365
      row.OverdueCharges = row.OutstandingBalance * (overdueRate / 365);

      prevBalance = row.OutstandingBalance;
    }

    setScheduleData(updated);
  };

  // ➕ Add new row
  const handleAddRow = () => {
    const newRow = {
      Date: "",
      Disbursement: 0,
      Repayment: 0,
      OutstandingBalance: 0,
      InterestAccrued: 0,
      OverdueCharges: 0,
    };
    setScheduleData([...scheduleData, newRow]);
  };

  // ❌ Delete a specific row
  const handleDeleteRow = (index) => {
    if (window.confirm("Are you sure you want to delete this row?")) {
      const updated = scheduleData.filter((_, i) => i !== index);
      // Recalculate balances after deletion
      let prevBalance = 0;
      updated.forEach((row) => {
        row.OutstandingBalance =
          prevBalance + (row.Disbursement || 0) - (row.Repayment || 0);
        row.InterestAccrued =
          row.OutstandingBalance *
          (Number(transactionData?.INTERESTRATE || 0) / 100 / 365);
        row.OverdueCharges = row.OutstandingBalance * (0.1 / 365);
        prevBalance = row.OutstandingBalance;
      });
      setScheduleData(updated);
    }
  };

  const handleCancel = () => {
    setFormData(transactionData);
    setChecklistFormData(transactionData?.DocumentCheckList || []);
    setIsEditing(false);
  };
  const totalDisbursement = scheduleData.reduce(
    (sum, row) => sum + Number(row.Disbursement || 0),
    0
  );
  const totalRepayment = scheduleData.reduce(
    (sum, row) => sum + Number(row.Repayment || 0),
    0
  );
  const totalOutstanding = totalDisbursement - totalRepayment;
  const totalInterestAccrued = scheduleData.reduce(
    (sum, row) => sum + Number(row.InterestAccrued || 0),
    0
  );
  const totalOverdueCharges = scheduleData.reduce(
    (sum, row) => sum + Number(row.OverdueCharges || 0),
    0
  );
  const adminFeeRate = Number(transactionData?.ADMINFEE || 0); // e.g., 0.05 for 5%
  const feesAccrued = totalDisbursement * adminFeeRate;

  const recomputeAll = (rows) => {
    // interestRate and overdueRate should be decimals (e.g. 0.12 for 12%)
    const interestRate = Number(transactionData?.INTERESTRATE ?? 0); // $C$13
    const overdueRate = Number(transactionData?.OVERDUECHARGE ?? 0.1); // $C$14 fallback to 10%

    const updated = [];
    let prevBalance = 0; // start from zero (no preloaded loan amount)

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] || {};

      // ensure numeric values (blank => 0)
      const disb = parseFloat(r.Disbursement) || 0;
      const rep = parseFloat(r.Repayment) || 0;

      const outBal = prevBalance + disb - rep;
      const interest = outBal * (interestRate / 365);
      const overdue = outBal * (overdueRate / 365);

      // push normalized row
      updated.push({
        ...r,
        Disbursement: disb,
        Repayment: rep,
        OutstandingBalance: outBal,
        InterestAccrued: interest,
        OverdueCharges: overdue,
      });

      prevBalance = outBal; // for next iteration
    }

    return updated;
  };

  const criteriaLabels = {
    COMPANYNAMEISCORRECT: "Company name is correct",
    THEREGISTRATIONISCURRENT: "The registration is current",
    THERETURNCOVERSTHEMOSTRECENTPERIOD:
      "The return covers the most recent period",
    SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED:
      "Shareholder(s) match organization chart (if provided)",
    THECHARTISSIGNEDANDDATED: "The chart is signed and dated",
    STATEMENTSARESIGNEDBYAUDITOR: "Statements are signed by auditor",
  };
  const grandTotal =
    totalOutstanding + totalInterestAccrued + feesAccrued + totalOverdueCharges;

  const renderCheckCell = (section) => {
    // if (section === "SUBMITTEDBUSINESSREGISTRATION") return "";

    // Show the label for these fields instead of CHECKINGDETAILS
    if (
      section === "COMPANYNAMEISCORRECT" ||
      section === "THEREGISTRATIONISCURRENT" ||
      section === "THERETURNCOVERSTHEMOSTRECENTPERIOD" ||
      section === "SHAREHOLDER_SMATCHORGANIZATIONCHART_IFPROVIDED" ||
      section === "THECHARTISSIGNEDANDDATED" ||
      section === "STATEMENTSARESIGNEDBYAUDITOR"
    ) {
      return criteriaLabels[section] || "";
    }

    if (section === "ENTEREXPIRYDATE") {
      const rawDate = formData?.ENTEREXPIRYDATE?.CHECKINGDETAILS;
      return rawDate ? formatDate(rawDate) : "";
    }

    return formData?.[section]?.CHECKINGDETAILS || "";
  };

  const handleSubmit = async () => {
    try {
      // example save logic — adjust API endpoint
      const response = await fetch(`${API_URL}/saveInterestSchedule/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(scheduleData),
      });
      if (!response.ok) throw new Error("Failed to save schedule");
      toast.success("Schedule saved successfully!");
      setIsEditing(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Utility: format date values into DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return dateString; // fallback if invalid date
    return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
  };
  const today = new Date();
  const formatted = today.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long", // 👈 gives full month name
    year: "numeric",
  });

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <Header />
        </main>
      </div>

      <div className="container">
        <div className="row mt-4">
          <div className="col-md-12">
            <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
              Back to Transaction List
            </NavLink>

            <br />
            <br />
            <NavLink
              to={`/ViewTransactionList/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Loan Details
            </NavLink>

            <NavLink
              to={`/Drawdowns/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Drawdowns
            </NavLink>

            <NavLink
              to={`/ApplicationChecklist/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Application Checklist
            </NavLink>

            <NavLink
              to={`/ScoreCard/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scorecard
            </NavLink>

            <NavLink
              to={`/TransactionSummary/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Transaction Summary
            </NavLink>

            <NavLink
              to={`/TermSheet/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Term Sheet
            </NavLink>

            <NavLink
              to={`/Payments/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Payments
            </NavLink>
            <NavLink
              to={`/RepaymentAndInterest/${id}`}
              className="btn btn-dark ms-2"
            >
              Repayment & Interest(Term)
            </NavLink>
            <NavLink
              to={`/InterestCalculations/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Interest Calculations
            </NavLink>
            <NavLink
              to={`/ScoringGuide/${id}`}
              className="btn btn-outline-secondary ms-2"
            >
              Scoring Guide
            </NavLink>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ marginTop: "15px" }}>Interest Calculations</h3>
              <div>
                {isEditing && (
                  <button
                    className="btn btn-secondary me-2 btn-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                )}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={async () => {
                    if (isEditing) {
                      setIsSubmitting(true);
                      await handleSubmit();
                      setIsSubmitting(false);
                    } else {
                      setFormData(transactionData);
                      setIsEditing(true);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  {isEditing
                    ? isSubmitting
                      ? "Submitting..."
                      : "Submit"
                    : "Edit"}
                </button>
              </div>
            </div>

            {loading || !transactionData ? (
              <ShimmerTable row={5} col={5} />
            ) : (
              <>
                <div className="col-md-12">
                  <table>
                    <tbody>
                      <tr>
                        <td>{transactionData?.BORROWER_LABEL || "BORROWER"}</td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.BORROWER || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          {transactionData?.TRANSACTIONID_LABEL ||
                            "Transaction ID"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.TRANSACTIONID || "N/A"}
                        </td>
                      </tr>
                      <br></br>
                      <tr>
                        <td>
                          {transactionData?.LOANAMOUNT_HK_LABEL ||
                            "Loan Amount"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.LOANAMOUNT_HK || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>{transactionData?.TRANCHE1_LABEL || "TRANCHE1"}</td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.TRANCHE1 || "0"}
                        </td>
                      </tr>
                      <tr>
                        <td>{transactionData?.TRANCHE2_LABEL || "TRANCHE2"}</td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.TRANCHE2 || "0"}
                        </td>
                      </tr>
                      <br></br>
                      <tr>
                        <td>
                          {transactionData?.MAXTERM_MONTHS_LABEL ||
                            "Term(Months)"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.MAXTERM_MONTHS || "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          {transactionData?.INTERESTRATE_LABEL ||
                            "Interest Rate(p.a)"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.INTERESTRATE
                            ? (transactionData?.INTERESTRATE * 100).toFixed(2) +
                              "%"
                            : "N/A"}
                        </td>
                      </tr>
                      <br></br>
                      <tr>
                        <td>
                          {transactionData?.ADMINFEE_LABEL || "Admin Fee"}
                        </td>
                        <td style={{ paddingLeft: "40px" }}>
                          {transactionData?.ADMINFEE
                            ? (transactionData?.ADMINFEE * 100).toFixed(2) + "%"
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <br></br>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: "150px" }}></th>
                        <th
                          style={{
                            backgroundColor: "#1c6ae0ff",
                            width: "150px",
                            textAlign: "center",
                          }}
                        >
                          Disbursement
                        </th>
                        <th
                          style={{
                            backgroundColor: "#1c6ae0ff",
                            width: "150px",
                            textAlign: "center",
                          }}
                        >
                          Repayment
                        </th>
                        <th
                          style={{
                            backgroundColor: "#1c6ae0ff",
                            width: "250px",
                            textAlign: "center",
                          }}
                        >
                          Outstanding Balance
                        </th>
                        <th
                          style={{
                            backgroundColor: "#1c6ae0ff",
                            width: "150px",
                            textAlign: "center",
                          }}
                        >
                          Interest Accrued
                        </th>
                        <th
                          style={{
                            backgroundColor: "#1c6ae0ff",
                            width: "150px",
                            textAlign: "center",
                          }}
                        >
                          Fees Accrued
                        </th>
                        <th
                          style={{
                            backgroundColor: "#1c6ae0ff",
                            width: "150px",
                            textAlign: "center",
                          }}
                        >
                          Overdue Charges
                        </th>
                        <th
                          style={{
                            backgroundColor: "#1c6ae0ff",
                            width: "150px",
                            textAlign: "center",
                          }}
                        >
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ textAlign: "center", fontWeight: "bold" }}>
                        <td>Total</td>
                        <td>{totalDisbursement.toFixed(2)}</td>
                        <td>{totalRepayment.toFixed(2)}</td>
                        <td>{totalOutstanding.toFixed(2)}</td>
                        <td>{totalInterestAccrued.toFixed(2)}</td>
                        <td>{feesAccrued.toFixed(2)}</td>
                        <td>{totalOverdueCharges.toFixed(2)}</td>
                        <td>{grandTotal.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <br></br>
                  {/* Interest Calculation Table */}
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped">
                      <thead className="table-primary text-center align-middle">
                        <tr>
                          <th style={{ width: "10%" }}>Date</th>
                          <th style={{ width: "15%" }}>Disbursement</th>
                          <th style={{ width: "15%" }}>Repayment</th>
                          <th style={{ width: "20%" }}>Outstanding Balance</th>
                          <th style={{ width: "20%" }}>Interest Accrued</th>
                          <th style={{ width: "15%" }}>Overdue Charges</th>
                          {isEditing && <th style={{ width: "5%" }}>Action</th>}
                        </tr>
                      </thead>

                      <tbody>
                        {scheduleData.map((row, index) => (
                          <tr key={index}>
                            <td>
                              <input
                                type="date"
                                value={row.Date || ""}
                                disabled={!isEditing}
                                min={contractStartDate}
                                max={contractEndDate}
                                onChange={(e) =>
                                  handleChange(index, "Date", e.target.value)
                                }
                                className="form-control"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={row.Disbursement || ""}
                                disabled={!isEditing}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    "Disbursement",
                                    e.target.value
                                  )
                                }
                                className="form-control text-end"
                              />
                            </td>
                            <td>
                              <input
                                type="number"
                                value={row.Repayment || ""}
                                disabled={!isEditing}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    "Repayment",
                                    e.target.value
                                  )
                                }
                                className="form-control text-end"
                              />
                            </td>
                            <td className="text-end">
                              {Number(row.OutstandingBalance || 0).toFixed(2)}
                            </td>
                            <td className="text-end">
                              {Number(row.InterestAccrued || 0).toFixed(2)}
                            </td>
                            <td className="text-end">
                              {Number(row.OverdueCharges || 0).toFixed(2)}
                            </td>

                            {isEditing && (
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteRow(index)}
                                >
                                  ❌
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Action Buttons */}
                    {isEditing && (
                      <div className="d-flex justify-content-end mt-2">
                        <button
                          className="btn btn-sm btn-success"
                          onClick={handleAddRow}
                        >
                          ➕ Add Row
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddRow}
                      disabled={!isEditing}
                    >
                      + Add Row
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
